import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { motion } from 'framer-motion'
import moment from 'moment'
import { useCountUp } from '../../../userPages/dashboard/managerOverview/overviewTheme'

const GlassChip = ({ label, value, accent }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{
      px: 1.5,
      py: 0.75,
      borderRadius: 5,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.14)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: accent ?? '#4DBBC4',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.95rem' }}>
      {value}
    </Typography>
    <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.8rem' }}>
      {label}
    </Typography>
  </Stack>
)

const WorkforceHero = ({
  orgSettings,
  summary,
  pendingReviews,
  actionsTotal,
}) => {
  const activeStaff = useCountUp(summary?.activeStaffCount ?? 0)

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        px: { xs: 3, md: 5 },
        py: { xs: 4, md: 5 },
        color: '#FFFFFF',
        background:
          'linear-gradient(120deg, #1A2230 0%, #242F40 55%, #1E3A45 100%)',
      }}
    >
      {/* ambient glows + fine dot grid, matching the command briefing hero */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(600px 300px at 85% -20%, rgba(35, 154, 164, 0.35), transparent 70%),' +
            'radial-gradient(500px 260px at -10% 110%, rgba(204, 164, 59, 0.16), transparent 70%),' +
            'radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)',
          backgroundSize: 'auto, auto, 22px 22px',
        }}
      />

      <Stack
        position="relative"
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={3}
      >
        <Stack gap={1}>
          <Typography
            sx={{
              color: '#4DBBC4',
              fontWeight: 600,
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
            }}
          >
            {orgSettings?.orgName ?? 'Company'} · Workforce Briefing
          </Typography>
          <Typography
            sx={{
              color: '#FFFFFF',
              fontWeight: 600,
              fontSize: { xs: '1.7rem', md: '2.2rem' },
              lineHeight: 1.15,
            }}
          >
            Your workforce today
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            {moment().format('dddd D MMMM YYYY')} — here's where your people
            stand right now.
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1.5}>
            <GlassChip
              label={`new starter${summary?.newStarters === 1 ? '' : 's'}`}
              value={summary?.newStarters ?? 0}
            />
            <GlassChip
              label={`applicant${summary?.applicants === 1 ? '' : 's'} awaiting review`}
              value={summary?.applicants ?? 0}
            />
            <GlassChip
              label="reviews waiting on you"
              value={pendingReviews ?? 0}
              accent={(pendingReviews ?? 0) > 0 ? '#F28B1F' : '#4DBBC4'}
            />
            <GlassChip
              label={`compliance action${actionsTotal === 1 ? '' : 's'}`}
              value={actionsTotal ?? 0}
              accent={(actionsTotal ?? 0) > 0 ? '#F28B1F' : '#4DBBC4'}
            />
          </Stack>
        </Stack>

        <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0}>
          <Typography
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '3.4rem', md: '4.4rem' },
              lineHeight: 1,
              color: '#FFFFFF',
            }}
          >
            {activeStaff}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
            active staff on the books
          </Typography>
        </Stack>
      </Stack>
    </Box>
  )
}

export default WorkforceHero
